import { FormikErrors } from "formik";
import React, { SetStateAction, useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import PhoneInput, {
  isPossiblePhoneNumber,
  getCountries,
} from "react-phone-number-input";
import errorIcon from "assets/Icon_Notification_Warning.png";
import { customUseOnClickOutside } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";

const CountryOptions = ({ options, handleOnClick }: any) => {
  return (
    <div className="CountryOptions">
      {options &&
        Array.isArray(options) &&
        options.length > 1 &&
        options.map((option: any, idx: number) => {
          return (
            <div
              className="Option"
              onClick={() => {
                if (option.value) {
                  handleOnClick(option.value);
                }
              }}
              key={idx}
            >
              {option.label && option.label}
            </div>
          );
        })}
    </div>
  );
};

const CustomCountryOptions = ({ props, ...helpers }: any) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [countryName, setCountryName] = useState<string | null>(null);

  const handleOnClick = (value: string) => {
    if (value && value.toLowerCase() !== "international" && helpers.onChange) {
      setShowOptions(false);

      helpers.onChange(value);
    }
  };

  const bind = customUseOnClickOutside(() => setShowOptions(false), true);

  useEffect(() => {
    if (
      helpers.value &&
      helpers.options &&
      Array.isArray(helpers.options) &&
      helpers.options.length > 0
    ) {
      const filteredCountryNames = helpers.options.filter(
        (item: { value: string; label: string }) => item.value === helpers.value
      );

      if (filteredCountryNames.length === 1) {
        setCountryName(filteredCountryNames[0].label);
      }
    }
  }, [helpers]);

  return (
    <div className="CustomCountryOptions">
      <div className="Icon" onClick={() => setShowOptions(true)}>
        {helpers.value && countryName && (
          <helpers.iconComponent
            country={helpers.value}
            // countryName={countryName}
            label={countryName}
          />
        )}
      </div>

      <div className={`Options ${!showOptions ? "Disabled" : ""}`} {...bind}>
        {helpers.options && showOptions && (
          <CountryOptions
            options={helpers.options}
            handleOnClick={handleOnClick}
          />
        )}
      </div>
    </div>
  );
};

type PhoneNumberFieldProps = {
  value: string | undefined;
  onChange: (phoneNumber: string) => void;
  formikFieldName?: string;
  formikErrors?: FormikErrors<{ [key: string]: string }>;
  setFormikErrors?: (errors: FormikErrors<{ [key: string]: string }>) => void;
  submitted?: boolean;
  showErrorsAfterBlur?: boolean;
  showErrorsAfterSubmit?: boolean;
  disabled?: boolean;
  hideErrorMessages?: boolean;
  noPhoneNumberError?: string;
  parentError?: boolean;
  setParentError?: React.Dispatch<SetStateAction<boolean>>;
  showErrorsImmediately?: boolean;
  phoneNumberRequired?: boolean;
};

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  formikFieldName,
  formikErrors,
  setFormikErrors,
  submitted = true,
  showErrorsAfterBlur = false,
  showErrorsAfterSubmit = false,
  hideErrorMessages = false,
  noPhoneNumberError,
  disabled,
  parentError,
  setParentError,
  showErrorsImmediately = false,
  phoneNumberRequired = true,
  ...props
}) => {
  const [errors, setErrors] = useState<string | undefined>(undefined);
  const [focused, setFocused] = useState<boolean>(false);
  const [userInteracted, setUserInteracted] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [countriesList, setCountriesList] = useState<any | null>(null); // should be CountryOption[] | null but can't get CountryOption type from package

  useEffect(() => {
    let tempCountriesList = getCountries();
    tempCountriesList = tempCountriesList.filter(item => item !== "GB");
    tempCountriesList = ["GB", ...tempCountriesList];
    setCountriesList(tempCountriesList);
  }, []);

  useEffect(() => {
    const tempShowErrors =
      (showErrorsAfterBlur && userInteracted) ||
      (showErrorsAfterSubmit && submitted) ||
      showErrorsImmediately;

    setShowErrors(tempShowErrors);
  }, [
    showErrorsAfterBlur,
    userInteracted,
    showErrorsAfterSubmit,
    submitted,
    showErrorsImmediately,
  ]);

  // `value` will be the parsed phone number in E.164 format.
  // Example: "+12133734253".
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);

  const possibleErrors = {
    invalidPhoneNumber:
      "Invalid phone number. Please check the country code and phone number are correct.",
    noPhoneNumber: noPhoneNumberError ?? "Please provide a phone number",
  };

  useEffect(() => {
    setPhoneNumber(value);
  }, [value]);

  const updateErrors = (newError: string | undefined) => {
    if (errors !== newError) {
      setErrors(newError);
    }

    if (
      formikFieldName &&
      formikErrors &&
      formikErrors[formikFieldName] !== newError &&
      setFormikErrors
    ) {
      if (
        newError === undefined &&
        formikErrors[formikFieldName] !== undefined
      ) {
        let tempFormikErrors = { ...formikErrors };
        tempFormikErrors[formikFieldName] = undefined;
        setFormikErrors(tempFormikErrors);
      } else {
        let tempFormikErrors = { ...formikErrors };
        tempFormikErrors[formikFieldName] = `${errors}`;
        setFormikErrors(tempFormikErrors);
      }
    }

    if (parentError !== undefined && setParentError) {
      if (newError) {
        setParentError(true);
      } else {
        setParentError(false);
      }
    }
  };

  useEffect(() => {
    if (value) {
      if (value === "") {
        setPhoneNumber(undefined);
      } else {
        setPhoneNumber(value);
      }
    }
  }, [value]);

  const validateNumber = () => {
    phoneNumber && phoneNumber !== ""
      ? // ? isValidPhoneNumber(phoneNumber)
        isPossiblePhoneNumber(phoneNumber)
        ? updateErrors(undefined)
        : updateErrors(possibleErrors.invalidPhoneNumber)
      : phoneNumberRequired
      ? updateErrors(possibleErrors.noPhoneNumber)
      : updateErrors(undefined);
  };

  return (
    <div
      className={`PhoneNumberInput 
      ${showErrors && errors ? "Errors" : ""} 
      ${focused ? "Focused" : ""}`}
      onBlur={() => {
        setFocused(false);
        setUserInteracted(true);
      }}
      onFocus={() => setFocused(true)}
    >
      <PhoneInput
        defaultCountry="GB"
        placeholder="Enter phone number"
        value={value}
        onChange={onChange}
        error={validateNumber()}
        international
        addInternationalOption={false}
        countrySelectComponent={CustomCountryOptions}
        countryOptionsOrder={countriesList ? countriesList : undefined}
        // internationalIcon={} // You can add a custom international number icon using this
        countries={countriesList ? countriesList : undefined}
        disabled={disabled ? disabled : undefined}
      />

      {!hideErrorMessages && showErrors && errors && (
        <div className="Errors">
          <div className="Text">{errors}</div>
          <img src={errorIcon} alt="error" />
        </div>
      )}
    </div>
  );
};

export default PhoneNumberField;
